import { useEffect, useState } from 'react';
import { Autocomplete, Grid, TextField, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { createAutojob, createAutojob2, CreateAutojob2Request } from 'src/shared/services/autojobService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllProfileGroups, getProfileGroupByIdAllItems } from 'src/shared/services/profileGroupService';
import { getDistricts } from 'src/shared/services/districtService';
import useDistrictPickerHeader from 'src/shared/hooks/useDistrictPickerHeader';
import { AutojobType, autojobTypeOptions, SnapshotJobType } from './selectAutojobTypeModal';
import { versionOptions } from './createAutojobModal';

type ProfileItemOption = {
  id: number;
  groupId: number;
  name: string;
  groupTitle: string;
};

type DistrictOption = {
  id: number;
  name: string;
};

type AssociationOption = {
  id: number;
  name: string;
  districtId: number;
  districtName: string;
};

// Varje profil blir två kommaseparerade värden: memberProfile_id,memberProfileItem_id.
const toProfileValue = (profiles: ProfileItemOption[]) => profiles.flatMap((p) => [p.groupId, p.id]).join(',');

type MemberType = 'member' | 'interest' | 'other' | 'resigned';

const memberTypeOptions: { value: MemberType; labelKey: string }[] = [
  { value: 'member', labelKey: 'settings:autojobs.memberTypes.member' },
  { value: 'interest', labelKey: 'settings:autojobs.memberTypes.interested' },
  { value: 'other', labelKey: 'settings:autojobs.memberTypes.other' },
  { value: 'resigned', labelKey: 'settings:autojobs.memberTypes.resigned' },
];

// Utträdesorsak – väljs endast när typ av medlem är Utträdd.
type ResignReason = '' | 'notPaid';

const resignReasonOptions: { value: ResignReason; labelKey: string }[] = [
  { value: '', labelKey: 'settings:autojobs.resignReasons.none' },
  { value: 'notPaid', labelKey: 'settings:autojobs.resignReasons.notPaid' },
];

type AmountType = 'd' | 'y';

const amountTypeOptions: { value: AmountType; labelKey: string }[] = [
  { value: 'd', labelKey: 'settings:autojobs.amountTypes.days' },
  { value: 'y', labelKey: 'settings:autojobs.amountTypes.years' },
];

type CountFrom = 'memberExpireDate' | 'invoiceExpireDate' | 'createDate' | 'resDate';

const countFromOptions: { value: CountFrom; labelKey: string }[] = [
  { value: 'memberExpireDate', labelKey: 'settings:autojobs.countFrom.memberDueDate' },
  { value: 'invoiceExpireDate', labelKey: 'settings:autojobs.countFrom.invoiceDueDate' },
  { value: 'createDate', labelKey: 'settings:autojobs.countFrom.memberRegistrationDate' },
  { value: 'resDate', labelKey: 'settings:autojobs.countFrom.memberResignationDate' },
];

type AgeComparison = 'y' | 'o';

const ageComparisonOptions: { value: AgeComparison; labelKey: string }[] = [
  { value: 'y', labelKey: 'settings:autojobs.ageComparison.youngerThan' },
  { value: 'o', labelKey: 'settings:autojobs.ageComparison.olderThan' },
];

// Om åldern avser de som fyller antalet år under innevarande år, eller de som redan har fyllt det.
type AgeCalculation = 'y' | 'f';

const ageCalculationOptions: { value: AgeCalculation; labelKey: string }[] = [
  { value: 'y', labelKey: 'settings:autojobs.ageCalculation.turnsThisYear' },
  { value: 'f', labelKey: 'settings:autojobs.ageCalculation.hasTurned' },
];

type IncludeMissingBirthDate = 'true' | 'false';

const missingBirthDateOptions: { value: IncludeMissingBirthDate; labelKey: string }[] = [
  { value: 'true', labelKey: 'settings:autojobs.missingBirthDate.include' },
  { value: 'false', labelKey: 'settings:autojobs.missingBirthDate.exclude' },
];

export type FrequencyType = 'day' | 'week' | 'month' | 'year';

export const frequencyTypeOptions: { value: FrequencyType; labelKey: string }[] = [
  { value: 'day', labelKey: 'settings:autojobs.frequencyTypes.day' },
  { value: 'week', labelKey: 'settings:autojobs.frequencyTypes.week' },
  { value: 'month', labelKey: 'settings:autojobs.frequencyTypes.month' },
  { value: 'year', labelKey: 'settings:autojobs.frequencyTypes.year' },
];

// Jobbtyper som skapas via den här modalen – Custom och ögonblicksbilder har egna modaler.
export type SelectionAndFrequencyJobType = Exclude<AutojobType, 'custom' | SnapshotJobType>;

// Frekvensen motsvarar backendens schematyper (Dagligen, Veckovis, Månadsvis, Årligen).
export const frequencyTypeToScheduleType: Record<FrequencyType, number> = {
  day: 3,
  week: 4,
  month: 5,
  year: 6,
};

// Assembly per jobbtyp och version.
const assemblyNames: Record<SelectionAndFrequencyJobType, Record<1 | 2, string>> = {
  delete: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.DeleteMembers', 2: 'DeleteMembers' },
  resign: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.ResignMembers', 2: 'ResignMembers' },
  setProfile: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.AddProfiles', 2: 'AddProfiles' },
  removeProfile: { 1: 'YvnTaskPluginAutojob|YvnTaskPluginAutojob.RemoveProfiles', 2: 'RemoveProfiles' },
};

type CreateAutojobWithSelectionAndFrequencyModalProps = {
  open: boolean;
  jobType: SelectionAndFrequencyJobType;
  onClose: () => void;
  onSaved: () => void;
};

export const CreateAutojobWithSelectionAndFrequencyModal = (props: CreateAutojobWithSelectionAndFrequencyModalProps) => {
  const { open, jobType, onClose, onSaved } = props;
  const { t } = useTranslation(['settings', 'common']);
  const [version, setVersion] = useState<1 | 2>(2);
  const [memberType, setMemberType] = useState<MemberType>('member');
  const [resignReason, setResignReason] = useState<ResignReason>('');
  const [amount, setAmount] = useState(0);
  const [amountType, setAmountType] = useState<AmountType>('d');
  const [countFrom, setCountFrom] = useState<CountFrom>('memberExpireDate');
  const [ageComparison, setAgeComparison] = useState<AgeComparison>('y');
  const [ageYears, setAgeYears] = useState(0);
  const [ageCalculation, setAgeCalculation] = useState<AgeCalculation>('y');
  const [includeMissingBirthDate, setIncludeMissingBirthDate] = useState<IncludeMissingBirthDate>('false');
  const [frequency, setFrequency] = useState(1);
  const [frequencyType, setFrequencyType] = useState<FrequencyType>('day');
  const [startDate, setStartDate] = useState<Dayjs | null>(() => dayjs().add(0, 'minute'));
  const [startTime, setStartTime] = useState<Dayjs | null>(() => dayjs().add(0, 'minute'));

  const [profileOptions, setProfileOptions] = useState<ProfileItemOption[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [includeProfiles, setIncludeProfiles] = useState<ProfileItemOption[]>([]);
  const [excludeProfiles, setExcludeProfiles] = useState<ProfileItemOption[]>([]);
  // Profilen som jobbet sätter på eller tar bort från medlemmarna.
  const [targetProfile, setTargetProfile] = useState<ProfileItemOption | null>(null);
  const [targetProfileError, setTargetProfileError] = useState(false);
  const requiresTargetProfile = jobType === 'setProfile' || jobType === 'removeProfile';

  const [districtOptions, setDistrictOptions] = useState<DistrictOption[]>([]);
  const [associationOptions, setAssociationOptions] = useState<AssociationOption[]>([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [district, setDistrict] = useState<DistrictOption | null>(null);
  const [association, setAssociation] = useState<AssociationOption | null>(null);

  const [saving, setSaving] = useState(false);
  const isMounted = useIsMounted();
  const { userHasDistrict, districtName, associationName } = useDistrictPickerHeader();

  useEffect(() => {
    if (!open) return;
    setLoadingDistricts(true);
    getDistricts(0, 10000, 'countyAss', 'asc', true)
      .then((res) => {
        if (!isMounted()) return;
        const districts = res.data.results;
        setDistrictOptions(districts.map((d) => ({ id: d.countyAss_id, name: d.countyAss })));
        setAssociationOptions(
          districts.flatMap((d) =>
            (d.associations ?? []).map((a) => ({ id: a.localAss_id, name: a.localAss, districtId: d.countyAss_id, districtName: d.countyAss })),
          ),
        );
      })
      .catch((e) => ConsoleHelper.error(e))
      .finally(() => {
        if (isMounted()) setLoadingDistricts(false);
      });
  }, [open, isMounted]);

  // Distrikt och förening väljs oberoende av varandra – ett valt distrikt begränsar bara vilka föreningar som visas.
  const filteredAssociationOptions = district ? associationOptions.filter((a) => a.districtId === district.id) : associationOptions;

  const handleDistrictChange = (value: DistrictOption | null) => {
    setDistrict(value);
    if (value && association && association.districtId !== value.id) setAssociation(null);
  };

  useEffect(() => {
    if (!open) return;
    setLoadingProfiles(true);
    // getAllProfileGroups returnerar inte alla profiler – hämta dem per grupp.
    getAllProfileGroups(true, true)
      .then(async (res) => {
        const groups = await Promise.all(
          res.data.results.map((g) =>
            getProfileGroupByIdAllItems(g.memberProfile_id, true)
              .then((r) => r.data)
              .catch(() => g),
          ),
        );
        if (!isMounted()) return;
        setProfileOptions(
          groups.flatMap((g) =>
            (g.profileItems ?? []).map((i) => ({ id: i.memberProfileItem_id, groupId: g.memberProfile_id, name: i.name, groupTitle: g.title })),
          ),
        );
      })
      .catch((e) => ConsoleHelper.error(e))
      .finally(() => {
        if (isMounted()) setLoadingProfiles(false);
      });
  }, [open, isMounted]);

  // En profil kan inte både inkluderas och exkluderas.
  const includeProfileOptions = profileOptions.filter((opt) => !excludeProfiles.some((p) => p.id === opt.id));
  const excludeProfileOptions = profileOptions.filter((opt) => !includeProfiles.some((p) => p.id === opt.id));

  const jobTypeLabelKey = autojobTypeOptions.find((opt) => opt.value === jobType)?.labelKey;

  const handleSave = async () => {
    if (saving) return;
    const assemblyName = assemblyNames[jobType][version];
    if (!startDate?.isValid() || !startTime?.isValid()) return;
    if (requiresTargetProfile && !targetProfile) {
      setTargetProfileError(true);
      return;
    }

    const request: CreateAutojob2Request = {
      taskName: jobTypeLabelKey ? t(jobTypeLabelKey) : jobType,
      assemblyName,
      scheduleType: frequencyTypeToScheduleType[frequencyType],
      increaseNum: frequency,
      startTimeStamp: startDate.hour(startTime.hour()).minute(startTime.minute()).format('YYYY-MM-DDTHH:mm'),
      custom1: '',
      custom2: amount > 0 ? `${amount}|${amountType}|${countFrom}` : '',
      custom3: toProfileValue(includeProfiles),
      custom4: toProfileValue(excludeProfiles),
      custom5: memberType,
      custom6: memberType === 'resigned' && resignReason === 'notPaid' ? 'Ej betalt' : '',
      custom7: ageYears > 0 ? `${ageYears}|${ageComparison}|${ageCalculation}|${includeMissingBirthDate}` : '',
      custom8: `${district?.id ?? 0}|${association?.id ?? 0}`,
      custom9: requiresTargetProfile && targetProfile ? toProfileValue([targetProfile]) : '',
      custom10: '',
      custom11: '',
      custom12: '',
    };

    setSaving(true);
    try {
      await (version === 1 ? createAutojob(request) : createAutojob2(request));
      toast.success(<>{t('common:statusMessages.saveChangesSuccess')}</>);
      onSaved();
    } catch {
      toast.error(<>{t('common:statusMessages.saveChangesError')}</>);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GenericDialog
      open={open}
      dialogTitle={t('settings:autojobs.createModal.title')}
      dialogSubTitle={jobTypeLabelKey ? t(jobTypeLabelKey) : undefined}
      maxWidth='sm'
      onClose={onClose}
      onSave={handleSave}
      saveLabel='common:button.save'
      abortLabel='common:button.cancel'
      dialogContent={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.createModal.version')}
              value={version}
              onChange={(e) => setVersion(Number(e.target.value) as 1 | 2)}
              fullWidth
              size='small'
              select
            >
              {versionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {requiresTargetProfile && (
            <Grid item xs={12}>
              <Autocomplete
                loading={loadingProfiles}
                options={profileOptions}
                groupBy={(option) => option.groupTitle}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={targetProfile}
                onChange={(_, value) => {
                  setTargetProfile(value);
                  setTargetProfileError(false);
                }}
                size='small'
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(
                      jobType === 'setProfile' ? 'settings:autojobs.selectionModal.profileToAdd' : 'settings:autojobs.selectionModal.profileToRemove',
                    )}
                    required
                    error={targetProfileError}
                    helperText={targetProfileError ? t('common:validationMessages.required') : undefined}
                  />
                )}
              />
            </Grid>
          )}
          {userHasDistrict && (
            <Grid item xs={12}>
              <Autocomplete
                loading={loadingDistricts}
                options={districtOptions}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={district}
                onChange={(_, value) => handleDistrictChange(value)}
                size='small'
                renderInput={(params) => <TextField {...params} label={districtName} />}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Autocomplete
              loading={loadingDistricts}
              options={filteredAssociationOptions}
              groupBy={userHasDistrict && !district ? (option) => option.districtName : undefined}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={association}
              onChange={(_, value) => setAssociation(value)}
              size='small'
              renderInput={(params) => <TextField {...params} label={associationName} />}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.selectionModal.memberType')}
              value={memberType}
              onChange={(e) => setMemberType(e.target.value as MemberType)}
              fullWidth
              size='small'
              select
            >
              {memberTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {memberType === 'resigned' && (
            <Grid item xs={12}>
              <TextField
                label={t('settings:autojobs.selectionModal.resignReason')}
                value={resignReason}
                onChange={(e) => setResignReason(e.target.value as ResignReason)}
                fullWidth
                size='small'
                select
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                {resignReasonOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.amount')}
              value={amount}
              onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.amountType')}
              value={amountType}
              onChange={(e) => setAmountType(e.target.value as AmountType)}
              fullWidth
              size='small'
              select
            >
              {amountTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.selectionModal.countFrom')}
              value={countFrom}
              onChange={(e) => setCountFrom(e.target.value as CountFrom)}
              fullWidth
              size='small'
              select
            >
              {countFromOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.ageComparison')}
              value={ageComparison}
              onChange={(e) => setAgeComparison(e.target.value as AgeComparison)}
              fullWidth
              size='small'
              select
            >
              {ageComparisonOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.ageYears')}
              value={ageYears}
              onChange={(e) => setAgeYears(Math.max(0, Number(e.target.value)))}
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.selectionModal.ageCalculation')}
              value={ageCalculation}
              onChange={(e) => setAgeCalculation(e.target.value as AgeCalculation)}
              fullWidth
              size='small'
              select
            >
              {ageCalculationOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('settings:autojobs.selectionModal.missingBirthDate')}
              value={includeMissingBirthDate}
              onChange={(e) => setIncludeMissingBirthDate(e.target.value as IncludeMissingBirthDate)}
              fullWidth
              size='small'
              select
            >
              {missingBirthDateOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              loading={loadingProfiles}
              options={includeProfileOptions}
              groupBy={(option) => option.groupTitle}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={includeProfiles}
              onChange={(_, value) => setIncludeProfiles(value)}
              disableCloseOnSelect
              size='small'
              renderInput={(params) => <TextField {...params} label={t('settings:autojobs.selectionModal.includeProfiles')} />}
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              multiple
              loading={loadingProfiles}
              options={excludeProfileOptions}
              groupBy={(option) => option.groupTitle}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={excludeProfiles}
              onChange={(_, value) => setExcludeProfiles(value)}
              disableCloseOnSelect
              size='small'
              renderInput={(params) => <TextField {...params} label={t('settings:autojobs.selectionModal.excludeProfiles')} />}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.frequency')}
              value={frequency}
              onChange={(e) => setFrequency(Math.max(1, Number(e.target.value)))}
              fullWidth
              size='small'
              type='number'
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={t('settings:autojobs.selectionModal.frequencyType')}
              value={frequencyType}
              onChange={(e) => setFrequencyType(e.target.value as FrequencyType)}
              fullWidth
              size='small'
              select
            >
              {frequencyTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <DatePicker
              label={t('settings:autojobs.selectionModal.startDate')}
              value={startDate}
              onChange={(val) => setStartDate(val)}
              format='YYYY-MM-DD'
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              label={t('settings:autojobs.selectionModal.startTime')}
              value={startTime}
              onChange={(val) => setStartTime(val)}
              ampm={false}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            />
          </Grid>
        </Grid>
      }
    />
  );
};
